import {
  createEntityAdapter,
  createSelector,
  createSlice,
  isAnyOf,
} from '@reduxjs/toolkit';

import { participantsApi } from 'api/participants';
import { populationAttributesApi } from 'api/population_attributes';

import * as profileConstants from 'participant/participant_profile/constants';

const matchParticipantFetched = isAnyOf(
  participantsApi.endpoints.getParticipant.matchFulfilled,
);
const matchPopulationAttributesFetched = isAnyOf(
  populationAttributesApi.endpoints.getPopulationAttributes.matchFulfilled,
);

const populationAttributesAdapter = createEntityAdapter({
  selectId: (attribute) => attribute.slug,
});

export const participantProfileSlice = createSlice({
  name: 'participantProfile',
  initialState: {
    dirtyPages: [],
    participant: undefined,
    populationAttributes: populationAttributesAdapter.getInitialState(),
    redirectTo: undefined,
  },
  reducers: {
    SET_DIRTY_PAGE: (state, action) => {
      state.dirtyPages = [
        ...new Set([...state.dirtyPages, action.payload.pageName]),
      ];
    },
    SET_REDIRECT_TO: (state, action) => {
      state.redirectTo = action.payload.redirectTo;
    },
  },
  extraReducers(builder) {
    builder.addMatcher(matchParticipantFetched, (state, action) => ({
      ...state,
      participant: action.payload,
    }));
    builder.addMatcher(matchPopulationAttributesFetched, (state, action) => {
      populationAttributesAdapter.upsertMany(
        state.populationAttributes,
        action.payload,
      );
    });
  },
});

export const { SET_DIRTY_PAGE, SET_REDIRECT_TO } =
  participantProfileSlice.actions;

export const participantProfileSliceReducer = participantProfileSlice.reducer;

export const selectParticipant = (state) =>
  state.participantProfile.participant;
export const selectRedirectTo = (state) => state.participantProfile.redirectTo;

export const {
  selectAll: selectAllAttributes,
  selectById: selectAttributeBySlug,
  selectEntities: selectAttributes,
} = populationAttributesAdapter.getSelectors(
  (state) => state.participantProfile.populationAttributes,
);

// The savings/investment accounts attribute is country-specific, so its slug
// varies by country. Match on the shared suffix so this finds whichever
// country's variant the backend returned without having to list each country
const SAVINGS_INVESTMENT_ACCOUNTS_SLUG_SUFFIX = '--savings_investment_accounts';

export const selectSavingsInvestmentAccountsAttributes = createSelector(
  [selectAllAttributes],
  (attributes) =>
    attributes.filter((attribute) =>
      attribute.slug.endsWith(SAVINGS_INVESTMENT_ACCOUNTS_SLUG_SUFFIX),
    ),
);

const MOBILE_PHONE_CARRIER_SLUG_SUFFIX = '--mobile_phone_carrier';

export const selectMobileCarrierAttributes = createSelector(
  [selectAllAttributes],
  (attributes) =>
    attributes.filter((attribute) =>
      attribute.slug.endsWith(MOBILE_PHONE_CARRIER_SLUG_SUFFIX),
    ),
);

const HOUSEHOLD_INCOME_SLUG_SUFFIX = '--household_income';

export const selectRegionalHouseholdIncomeAttributes = createSelector(
  [selectAllAttributes],
  (attributes) =>
    attributes.filter((attribute) =>
      attribute.slug.endsWith(HOUSEHOLD_INCOME_SLUG_SUFFIX),
    ),
);

export const selectAttributeBySlugWithMetadata = createSelector(
  [selectAttributeBySlug],
  (attribute) => {
    // Population attributes are country-specific, so a given slug may not exist
    // for this participant. Return undefined rather than dereferencing it.
    if (!attribute) return undefined;

    return {
      ...attribute,
      defaultOptionName:
        profileConstants.attributeSlugsToDefaultOptionNames[attribute.slug],
      inputLabel:
        profileConstants.attributeSlugsToInputLabels[attribute.slug] ||
        attribute.name,
    };
  },
);

export const selectEmploymentAttribute = (state) =>
  selectAttributeBySlugWithMetadata(
    state,
    profileConstants.EMPLOYMENT_STATUS_SLUG,
  );

const selectDirtyPages = (state) => state.participantProfile.dirtyPages;

export const selectPageIsDirty = createSelector(
  [selectDirtyPages, (_, pageName) => pageName],
  (dirtyPages, pageName) => dirtyPages.includes(pageName),
);
